import axios from 'axios'
import router from '../router'

// Set base URL
axios.defaults.baseURL = process.env.VUE_APP_BE_APP_BASE_URL || 'http://localhost:2121'

// Flag to prevent multiple refresh attempts
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

// Request interceptor - add access token to every request
axios.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('access_token')
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - handle token refresh and errors
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Check if error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      const errorCode = error.response?.data?.error?.code

      // If token is revoked (logout/password change), clear and redirect
      if (errorCode === 'TOKEN_REVOKED') {
        localStorage.removeItem('user')
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        router.push({ name: 'login' })
        return Promise.reject(error)
      }

      // If token is invalid/expired, try to refresh
      if (errorCode === 'INVALID_TOKEN' || errorCode === 'UNAUTHORIZED') {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          }).then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`
            return axios(originalRequest)
          }).catch(err => {
            return Promise.reject(err)
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        const refreshToken = localStorage.getItem('refresh_token')

        if (!refreshToken) {
          // No refresh token, redirect to login
          localStorage.removeItem('user')
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          router.push({ name: 'login' })
          return Promise.reject(error)
        }

        try {
          // Try to refresh the token
          const response = await axios.post('/refresh', {
            refresh_token: refreshToken
          })

          if (response.data.success && response.data.data) {
            const newAccessToken = response.data.data.access_token
            localStorage.setItem('access_token', newAccessToken)

            // Update user data if provided
            if (response.data.data.user) {
              localStorage.setItem('user', JSON.stringify(response.data.data.user))
            }

            // Update authorization header
            axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`
            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`

            // Process queued requests
            processQueue(null, newAccessToken)

            // Retry original request
            return axios(originalRequest)
          } else {
            throw new Error('Token refresh failed')
          }
        } catch (refreshError) {
          // Refresh failed, clear auth and redirect
          processQueue(refreshError, null)
          localStorage.removeItem('user')
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          router.push({ name: 'login' })
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }
    }

    return Promise.reject(error)
  }
)

export default axios
